import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Field, SelectField } from "./common";
import { BESOINS, ETAPES, ORIGINES, TAILLES, slug } from "../lib/constants";
import { useData } from "../context/DataContext";

const EMPTY = {
  contact_nom: "", contact_email: "", contact_tel: "", contact_poste: "", entreprise: "", secteur: "",
  taille: "TPE", besoins: [], origine: "Site web", description: "", montant_estime: 0, etape: "nouveau",
};

export const DemandeFormDialog = ({ open, onOpenChange, initial, onSaved }) => {
  const { save } = useData();
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(initial ? { ...EMPTY, ...initial } : EMPTY); }, [open, initial]);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const toggleBesoin = (b) => setF((p) => ({ ...p, besoins: p.besoins.includes(b) ? p.besoins.filter((x) => x !== b) : [...p.besoins, b] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.entreprise.trim() || !f.contact_nom.trim()) return toast.error("Entreprise et nom du contact sont obligatoires");
    setBusy(true);
    try {
      const res = await save("demandes", { ...f, montant_estime: Number(f.montant_estime) || 0 }, initial?.id);
      toast.success(initial ? "Demande mise à jour" : "Nouvelle demande enregistrée");
      onOpenChange(false);
      onSaved?.(res);
    } catch { toast.error("Erreur lors de l'enregistrement"); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="demande-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{initial ? "Modifier la demande" : "Nouvelle demande"}</DialogTitle>
          <DialogDescription>Fiche contact, entreprise, besoin et origine de la demande.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-6">
          <fieldset className="grid gap-4 sm:grid-cols-2">
            <legend className="kc-eyebrow mb-3">Contact</legend>
            <Field label="Nom complet *"><Input data-testid="demande-contact-nom-input" value={f.contact_nom} onChange={set("contact_nom")} placeholder="Prénom Nom" /></Field>
            <Field label="Fonction"><Input data-testid="demande-contact-poste-input" value={f.contact_poste} onChange={set("contact_poste")} placeholder="Gérant(e), Directeur…" /></Field>
            <Field label="Email"><Input data-testid="demande-contact-email-input" type="email" value={f.contact_email} onChange={set("contact_email")} placeholder="contact@entreprise.fr" /></Field>
            <Field label="Téléphone"><Input data-testid="demande-contact-tel-input" value={f.contact_tel} onChange={set("contact_tel")} placeholder="06 00 00 00 00" /></Field>
          </fieldset>
          <fieldset className="grid gap-4 sm:grid-cols-2">
            <legend className="kc-eyebrow mb-3">Entreprise</legend>
            <Field label="Raison sociale *"><Input data-testid="demande-entreprise-input" value={f.entreprise} onChange={set("entreprise")} placeholder="Nom de l'entreprise" /></Field>
            <Field label="Secteur"><Input data-testid="demande-secteur-input" value={f.secteur} onChange={set("secteur")} placeholder="Commerce, BTP, Services…" /></Field>
            <Field label="Taille"><SelectField testId="demande-taille-select" value={f.taille} onChange={set("taille")} options={TAILLES} /></Field>
            <Field label="Origine"><SelectField testId="demande-origine-select" value={f.origine} onChange={set("origine")} options={ORIGINES} /></Field>
          </fieldset>
          <fieldset className="space-y-4">
            <legend className="kc-eyebrow mb-3">Besoin</legend>
            <div className="flex flex-wrap gap-2">
              {BESOINS.map((b) => (
                <button type="button" key={b} data-testid={`demande-besoin-${slug(b)}`} onClick={() => toggleBesoin(b)}
                  className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${f.besoins.includes(b) ? "border-[var(--kc-navy)] bg-[var(--kc-navy)] text-white" : "border-[var(--kc-line)] bg-white text-[var(--kc-navy)] hover:border-[var(--kc-navy)]"}`}>
                  {b}
                </button>
              ))}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Montant estimé (€ HT)"><Input data-testid="demande-montant-input" type="number" min="0" value={f.montant_estime} onChange={set("montant_estime")} /></Field>
              <Field label="Étape"><SelectField testId="demande-etape-select" value={f.etape} onChange={set("etape")} options={ETAPES} /></Field>
            </div>
            <Field label="Description du besoin"><Textarea data-testid="demande-description-input" rows={3} value={f.description} onChange={set("description")} placeholder="Contexte, enjeux, attentes du client…" /></Field>
          </fieldset>
          <div className="flex justify-end gap-3 border-t border-[var(--kc-line)] pt-5">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="demande-form-cancel-button">Annuler</Button>
            <Button type="submit" disabled={busy} className="kc-btn-primary" data-testid="demande-form-submit-button">{initial ? "Enregistrer" : "Créer la demande"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
